import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../../api";
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { Button } from "../ui/button"
import ImportLogsCards from "./ImportLogsCards";
import EmptyState from "../shared/EmptyState";
import KpiCards from "../shared/KpiCards";
import { Inbox } from "lucide-react";

function ImportLogs() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    ref: "",
    search: "",
    limit: 50,
  });
  const [draftFilters, setDraftFilters] = useState({
    startDate: "",
    endDate: "",
    ref: "",
    search: "",
    limit: 50,
  });
  const debounceTimer = useRef(null);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await api.get('/importlogs', {
        params: filters,
      });
      setLogs(res.data);
    } catch (err) {
      console.error("Erreur récupération logs :", err.message);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]); // ✅ plus de warning ici

  // Debounce sur les champs texte (ref et search)
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setFilters(draftFilters);
    }, 500);
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftFilters.ref, draftFilters.search]);

  const renderStatus = (code) => {
    if (!code) return "⏸️";

    if (code === 202) return "⏳ En cours";
    if (code === 200) return "✅ Succès";
    if (code === 500) return "❌ Erreur";
    if (code === 503) return "🔁 Réessai";
    if (code === 100) return "ℹ️ Info";

    return code;
  };

  const inputCls = "w-full border border-input bg-background text-foreground p-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

    return (
        <Container className="py-6 md:py-10">
            <PageHeader title="Logs d'import" />

            {(() => {
                const errorCount = logs.filter(i => i.http_status === 500).length;
                const lastImport = logs.length > 0
                    ? new Date(Math.max(...logs.map(i => new Date(i.date)))).toLocaleString('fr-FR')
                    : '-';
                const importMetrics = [
                    { value: logs.length, label: 'Imports totaux' },
                    { value: errorCount, label: 'Erreurs' },
                    { value: lastImport, label: 'Dernier import' }
                ];
                return <KpiCards metrics={importMetrics} />;
            })()}

            {logs.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title="Aucun log trouvé"
          subtitle="Essayez d'ajuster les filtres pour voir les résultats."
          actionLabel="Réinitialiser les filtres"
          onAction={() => {
            if (debounceTimer.current) {
              clearTimeout(debounceTimer.current);
              debounceTimer.current = null;
            }
            const reset = { startDate: "", endDate: "", ref: "", search: "", limit: 50 };
            setDraftFilters(reset);
            setFilters(reset);
          }}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <input type="date" className={inputCls} value={draftFilters.startDate} onChange={e => setDraftFilters({ ...draftFilters, startDate: e.target.value })} />
            <input type="date" className={inputCls} value={draftFilters.endDate} onChange={e => setDraftFilters({ ...draftFilters, endDate: e.target.value })} />
            <input type="text" className={inputCls} placeholder="Référence (UUID)" value={draftFilters.ref} onChange={e => setDraftFilters({ ...draftFilters, ref: e.target.value })} />
            <input type="text" className={inputCls} placeholder="Recherche..." value={draftFilters.search} onChange={e => setDraftFilters({ ...draftFilters, search: e.target.value })} />
            <select className={inputCls} value={draftFilters.limit} onChange={e => setDraftFilters({ ...draftFilters, limit: parseInt(e.target.value) })}>
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} lignes</option>)}
            </select>
            <Button onClick={() => {
              if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
              }
              setFilters(draftFilters);
            }}>🔍 Filtrer</Button>
          </div>

          <div className="relative w-full overflow-auto rounded-md border hidden md:block">
            <table className="w-full caption-bottom text-sm">
              <thead className="[&_tr]:border-b bg-muted/50">
                <tr>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Date</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Référence</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Message</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {logs.map((log, i) => (
                  <tr key={i} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-4 align-middle">{new Date(log.date).toLocaleString()}</td>
                    <td className="p-4 align-middle">{log.uuid}</td>
                    <td className="p-4 align-middle">{renderStatus(log.http_status)}</td>
                    <td className="p-4 align-middle">{log.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden">
            <ImportLogsCards logs={logs} />
          </div>
        </>
      )}
    </Container>
  );
}

export default ImportLogs;
